import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import LeoDigestPanel from "@/components/LeoDigestPanel";
import {
  Brain,
  PaperPlaneTilt,
  Trash,
  Plus,
  CheckCircle,
  CircleNotch,
  ArrowsClockwise,
  ListChecks,
  Storefront,
} from "@phosphor-icons/react";

const RESALE_TRIGGER_RE = /\b(sell|selling|sale|resell|resale|list (for|on) ?(sale|ebay|facebook|craigslist|reverb)?|unload|offload|get rid of|move on from|liquidat\w*|consign\w*)\b/i;

const URGENCY_LABEL = {
  now: "Now",
  this_week: "This week",
  this_month: "This month",
  before_renewal: "Before renewal",
  before_estate_meeting: "Before attorney",
  someday: "Someday",
};

const URGENCY_COLOR = {
  now: "bg-[#6B1718] text-[#F9F8F6]",
  this_week: "bg-[#9C5012] text-[#F9F8F6]",
  this_month: "bg-[#1A3622] text-[#F9F8F6]",
  before_renewal: "bg-[#2E4D77] text-[#F9F8F6]",
  before_estate_meeting: "bg-[#5C5F58] text-[#F9F8F6]",
  someday: "bg-[#C9C4B8] text-[#0A192F]",
};

const KIND_LABEL = {
  maintenance: "Maintenance",
  valuation_refresh: "Re-valuation",
  documentation: "Documentation",
  dispensation: "Dispensation",
  insurance: "Insurance",
  other: "Other",
};

export default function StewardshipDrawer({ open, onOpenChange }) {
  const { activeLedgerId, canWrite } = useAuth();
  const [quickActions, setQuickActions] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null); // {conversation, messages}
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [creatingFor, setCreatingFor] = useState(null);
  const scrollRef = useRef(null);

  const loadList = useCallback(async () => {
    try {
      const r = await api.get("/stewardship/conversations");
      setConversations(r.data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadQuickActions = useCallback(async () => {
    try {
      const r = await api.get("/stewardship/quick-actions");
      setQuickActions(r.data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadQuickActions();
    loadList();
  }, [open, loadQuickActions, loadList]);

  // When the ledger changes, drop the active conversation (it lived in another book)
  useEffect(() => {
    setActiveConv(null);
  }, [activeLedgerId]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeConv?.messages?.length, busy]);

  const openConversation = async (convId) => {
    try {
      const r = await api.get(`/stewardship/conversations/${convId}`);
      setActiveConv(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load conversation.");
    }
  };

  const startPresetConversation = async (preset) => {
    setCreatingFor(preset.key);
    setBusy(true);
    try {
      const r = await api.post("/stewardship/conversations", { preset: preset.key });
      const conv = r.data.conversation;
      // If a seed message was sent, reload the full conversation to get both turns
      const full = await api.get(`/stewardship/conversations/${conv.id}`);
      setActiveConv(full.data);
      await loadList();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start conversation.");
    } finally {
      setBusy(false);
      setCreatingFor(null);
    }
  };

  const startBlankConversation = async () => {
    try {
      const r = await api.post("/stewardship/conversations", { preset: "ask" });
      setActiveConv({ conversation: r.data.conversation, messages: [] });
      await loadList();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start conversation.");
    }
  };

  const sendMessage = async () => {
    if (!draft.trim() || !activeConv || busy) return;
    const content = draft.trim();
    setDraft("");
    // Optimistic user turn
    const optimistic = {
      id: `temp-${Date.now()}`,
      role: "user",
      content,
      actions: [],
      created_at: new Date().toISOString(),
    };
    setActiveConv((prev) => ({ ...prev, messages: [...(prev?.messages || []), optimistic] }));
    setBusy(true);
    try {
      const r = await api.post(`/stewardship/conversations/${activeConv.conversation.id}/messages`, {
        content,
      });
      setActiveConv((prev) => {
        const cleaned = (prev?.messages || []).filter((m) => m.id !== optimistic.id);
        return {
          ...prev,
          messages: [...cleaned, r.data.user, r.data.assistant],
        };
      });
      await loadList();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "The analyst is temporarily unavailable.");
      // Roll back the optimistic turn
      setActiveConv((prev) => ({
        ...prev,
        messages: (prev?.messages || []).filter((m) => m.id !== optimistic.id),
      }));
    } finally {
      setBusy(false);
    }
  };

  const onApplyAction = async (msgId, action) => {
    if (!canWrite) {
      toast.error("Your role is read-only on this ledger.");
      return;
    }
    try {
      const r = await api.post(
        `/stewardship/messages/${msgId}/actions/${action.id}/apply`,
        {},
      );
      toast.success(`Added to maintenance — due ${r.data.due_date}.`);
      // Optimistically mark this action as applied
      setActiveConv((prev) => ({
        ...prev,
        messages: (prev?.messages || []).map((m) =>
          m.id === msgId
            ? {
                ...m,
                actions: (m.actions || []).map((a) =>
                  a.id === action.id ? { ...a, applied: true } : a,
                ),
              }
            : m,
        ),
      }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not apply action.");
    }
  };

  const onDeleteConversation = async (convId) => {
    if (!window.confirm("Delete this conversation and its message history?")) return;
    try {
      await api.delete(`/stewardship/conversations/${convId}`);
      toast.success("Conversation deleted.");
      if (activeConv?.conversation?.id === convId) setActiveConv(null);
      await loadList();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete.");
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showEmptyState = !activeConv;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl flex flex-col p-0 bg-[#F9F8F6]"
        data-testid="stewardship-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-[#E0DDD6] bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#1A3622] flex items-center justify-center border border-[#0F2818] shrink-0">
              <Brain size={18} weight="regular" color="#F9F8F6" />
            </div>
            <div className="min-w-0">
              <SheetTitle className="font-serif-display text-xl text-[#0A192F] leading-tight">
                Leo · Stewardship
              </SheetTitle>
              <SheetDescription className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                Your private estate analyst · reads the full ledger
              </SheetDescription>
            </div>
            {activeConv && (
              <button
                onClick={() => setActiveConv(null)}
                data-testid="steward-back-to-presets"
                className="ml-auto text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58] hover:text-[#1A3622]"
              >
                ← Conversations
              </button>
            )}
          </div>
        </SheetHeader>

        {showEmptyState ? (
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            {/* Preset cards */}
            <div>
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-3">
                Start with a Focus
              </div>
              <div className="grid grid-cols-1 gap-2" data-testid="steward-presets">
                {quickActions.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => (p.key === "ask" ? startBlankConversation() : startPresetConversation(p))}
                    disabled={busy}
                    data-testid={`steward-preset-${p.key}`}
                    className="text-left border border-[#E0DDD6] bg-white hover:border-[#1A3622] p-4 transition-colors disabled:opacity-60"
                  >
                    <div className="flex items-start gap-3">
                      {creatingFor === p.key ? (
                        <CircleNotch size={16} className="text-[#1A3622] mt-0.5 animate-spin shrink-0" />
                      ) : p.key === "ask" ? (
                        <Plus size={16} className="text-[#1A3622] mt-0.5 shrink-0" />
                      ) : (
                        <ListChecks size={16} className="text-[#1A3622] mt-0.5 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="text-sm text-[#0A192F] font-medium">{p.title}</div>
                        <div className="text-xs text-[#5C5F58] mt-0.5">{p.subtitle}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Past conversations */}
            {conversations.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                    Past Conversations · {conversations.length}
                  </div>
                  <button onClick={loadList} className="text-[#5C5F58] hover:text-[#1A3622]" title="Refresh">
                    <ArrowsClockwise size={12} />
                  </button>
                </div>
                <ul className="space-y-2" data-testid="steward-conversation-list">
                  {conversations.map((c) => (
                    <li
                      key={c.id}
                      className="border border-[#E0DDD6] bg-white flex items-stretch"
                      data-testid={`steward-conv-row-${c.id}`}
                    >
                      <button
                        onClick={() => openConversation(c.id)}
                        className="flex-1 text-left px-4 py-3 hover:bg-[#F9F8F6]"
                      >
                        <div className="text-sm text-[#0A192F] truncate">{c.title}</div>
                        <div className="text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58] mt-0.5">
                          {c.message_count || 0} turns · Updated {new Date(c.updated_at).toLocaleDateString()}
                        </div>
                      </button>
                      <button
                        onClick={() => onDeleteConversation(c.id)}
                        data-testid={`steward-conv-delete-${c.id}`}
                        title="Delete conversation"
                        className="px-3 text-[#6B1718] hover:bg-[#6B1718]/10 border-l border-[#E0DDD6]"
                      >
                        <Trash size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Leo's weekly digest control */}
            <LeoDigestPanel />
          </div>
        ) : (
          <ConversationView
            conv={activeConv}
            busy={busy}
            scrollRef={scrollRef}
            draft={draft}
            setDraft={setDraft}
            onKeyDown={onKeyDown}
            sendMessage={sendMessage}
            onApplyAction={onApplyAction}
            canWrite={canWrite}
            onOpenChange={onOpenChange}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function ConversationView({
  conv, busy, scrollRef, draft, setDraft, onKeyDown, sendMessage, onApplyAction, canWrite, onOpenChange,
}) {
  const messages = conv.messages || [];
  const lastUser = [...messages].reverse().find((m) => m.role === "user");
  const showResaleHint =
    RESALE_TRIGGER_RE.test(draft || "") ||
    RESALE_TRIGGER_RE.test(lastUser?.content || "");
  return (
    <>
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-6 py-6 space-y-5"
        data-testid="steward-messages"
      >
        <div className="border-b border-[#E0DDD6] pb-3">
          <div className="font-serif-display text-xl text-[#0A192F]">{conv.conversation.title}</div>
        </div>

        {messages.length === 0 && !busy && (
          <div className="text-sm text-[#5C5F58] italic">Ask Leo a question to begin…</div>
        )}

        {messages.map((m) =>
          m.role === "user" ? (
            <UserBubble key={m.id} content={m.content} />
          ) : (
            <AssistantBubble
              key={m.id}
              message={m}
              onApplyAction={onApplyAction}
              canWrite={canWrite}
            />
          ),
        )}

        {busy && <ThinkingBubble />}
      </div>

      <div className="border-t border-[#E0DDD6] bg-white px-4 py-3">
        {showResaleHint && (
          <Link
            to="/assets"
            onClick={() => onOpenChange?.(false)}
            data-testid="steward-resale-hint"
            className="mb-3 -mx-1 px-3 py-2 bg-[#F4E9D2] border border-[#C5A059] flex items-start gap-2 hover:bg-[#EFDFB8] transition-colors"
          >
            <Storefront size={14} className="text-[#5C3D08] mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-[#0A192F] font-medium">
                Selling something? Try Prep for Resale.
              </div>
              <div className="text-[11px] text-[#5C3D08] mt-0.5">
                Open any asset and tap <em>Sell Asset</em>. Leo will price, recommend a
                platform, audit photos, and draft your listing.
              </div>
            </div>
            <span className="text-[10px] font-mono-ledger uppercase tracking-[0.2em] text-[#5C3D08] self-center">
              Open ledger →
            </span>
          </Link>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about valuations, gaps, maintenance, dispensation…"
            rows={2}
            data-testid="steward-input"
            className="flex-1 resize-none border border-[#E0DDD6] focus:border-[#1A3622] focus:outline-none px-3 py-2 text-sm bg-white"
          />
          <button
            onClick={sendMessage}
            disabled={busy || !draft.trim()}
            data-testid="steward-send"
            className="bg-[#1A3622] text-[#F9F8F6] px-4 py-2.5 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50"
            title="Send"
          >
            <PaperPlaneTilt size={14} weight="bold" />
          </button>
        </div>
        <div className="text-[10px] font-mono-ledger uppercase tracking-[0.2em] text-[#5C5F58] mt-1.5">
          Enter to send · Shift+Enter for newline
        </div>
      </div>
    </>
  );
}

function UserBubble({ content }) {
  return (
    <div className="flex justify-end" data-testid="steward-user-msg">
      <div className="max-w-[85%] bg-[#1A3622] text-[#F9F8F6] px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed">
        {content}
      </div>
    </div>
  );
}

function AssistantBubble({ message, onApplyAction, canWrite }) {
  const actions = message.actions || [];
  return (
    <div className="space-y-3" data-testid="steward-assistant-msg">
      <div className="bg-white border border-[#E0DDD6] px-5 py-4">
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
          Leo
        </div>
        <div className="prose prose-sm max-w-none text-[#0A192F] [&>*:first-child]:mt-0">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
        </div>
      </div>

      {actions.length > 0 && (
        <div className="space-y-2">
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] pl-1">
            Suggested Actions · {actions.length}
          </div>
          <ul className="space-y-2">
            {actions.map((a) => (
              <li
                key={a.id}
                className="bg-white border border-[#E0DDD6] p-3"
                data-testid={`steward-action-${a.id}`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`px-1.5 py-0.5 text-[9px] font-mono-ledger uppercase tracking-[0.15em] shrink-0 ${URGENCY_COLOR[a.urgency] || URGENCY_COLOR.this_month}`}
                  >
                    {URGENCY_LABEL[a.urgency] || a.urgency}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-[#0A192F] font-medium">{a.title}</div>
                    {a.rationale && (
                      <div className="text-xs text-[#5C5F58] mt-0.5">{a.rationale}</div>
                    )}
                    <div className="text-[10px] font-mono-ledger uppercase tracking-[0.2em] text-[#5C5F58] mt-1">
                      {KIND_LABEL[a.kind] || a.kind}
                      {a.due_date && ` · due ${a.due_date}`}
                      {a.estimated_minutes && ` · ${a.estimated_minutes} min`}
                    </div>
                  </div>
                  <div className="shrink-0">
                    {a.applied ? (
                      <span
                        data-testid={`steward-action-applied-${a.id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#1A3622]"
                      >
                        <CheckCircle size={14} weight="fill" /> Applied
                      </span>
                    ) : (
                      <button
                        onClick={() => onApplyAction(message.id, a)}
                        disabled={!canWrite || a.kind === "other"}
                        data-testid={`steward-action-apply-${a.id}`}
                        title={
                          !canWrite
                            ? "Read-only role — cannot modify the ledger"
                            : a.kind === "other"
                              ? "This action kind isn't auto-applicable"
                              : "Add to maintenance schedule"
                        }
                        className="text-xs border border-[#1A3622] text-[#1A3622] px-2.5 py-1 hover:bg-[#1A3622] hover:text-[#F9F8F6] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#1A3622] transition-colors"
                      >
                        Apply
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ThinkingBubble() {
  return (
    <div className="bg-white border border-[#E0DDD6] px-5 py-4" data-testid="steward-thinking">
      <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2 flex items-center gap-2">
        <CircleNotch size={12} className="animate-spin" />
        Leo is reviewing your ledger…
      </div>
      <div className="text-xs text-[#5C5F58] italic">
        Reading assets, maintenance, dispensation directives, and documentation gaps.
      </div>
    </div>
  );
}
